import { api } from './client';
import type {
  PersonResponse,
  PersonEmailAssociation,
  PersonPhoneAssociation,
  PersonOrganizationRole,
  PersonAddressResponse,
  PersonAddressCreate,
  PersonAddressUpdate
} from '../types/api';

export const personsApi = {
  // Search persons
  async search(params?: {
    search?: string;
    organizationId?: number;
    roleType?: string;
    includeInDirectory?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.organizationId) queryParams.append('organizationId', params.organizationId.toString());
    if (params?.roleType) queryParams.append('roleType', params.roleType);
    if (params?.includeInDirectory !== undefined) {
      queryParams.append('includeInDirectory', params.includeInDirectory.toString());
    }
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const query = queryParams.toString();
    return api.get<any[]>(`/persons/search${query ? `?${query}` : ''}`);
  },

  // Get a single person by ID
  async get(personId: number): Promise<PersonResponse> {
    return api.get<PersonResponse>(`/persons/${personId}`);
  },

  // Create a new person
  async create(person: any): Promise<PersonResponse> {
    return api.post<PersonResponse>(`/persons`, person);
  },

  // Update a person
  async update(personId: number, updates: any): Promise<PersonResponse> {
    return api.put<PersonResponse>(`/persons/${personId}`, updates);
  },

  // Get person's identification codes
  async getIdentificationCodes(personId: number): Promise<any[]> {
    return api.get<any[]>(`/persons/${personId}/identificationCodes`);
  },

  // Add identification code
  async addIdentificationCode(personId: number, code: any): Promise<any> {
    return api.post<any>(`/persons/${personId}/identificationCodes`, code);
  },

  // Update identification code
  async updateIdentificationCode(personId: number, codeId: number, updates: any): Promise<any> {
    return api.put<any>(`/persons/${personId}/identificationCodes/${codeId}`, updates);
  },

  // Delete identification code
  async deleteIdentificationCode(personId: number, codeId: number): Promise<void> {
    return api.delete(`/persons/${personId}/identificationCodes/${codeId}`);
  },

  // ============ Email Management ============

  // Add email address
  async addEmail(personId: number, email: Partial<PersonEmailAssociation>): Promise<PersonEmailAssociation> {
    return api.post<PersonEmailAssociation>(`/persons/${personId}/emails`, email);
  },

  // Update email address
  async updateEmail(personId: number, emailId: number, updates: Partial<PersonEmailAssociation>): Promise<PersonEmailAssociation> {
    return api.put<PersonEmailAssociation>(`/persons/${personId}/emails/${emailId}`, updates);
  },

  // Delete email address
  async deleteEmail(personId: number, emailId: number): Promise<void> {
    return api.delete(`/persons/${personId}/emails/${emailId}`);
  },

  // ============ Phone Management ============

  // Add phone number
  async addPhone(personId: number, phone: Partial<PersonPhoneAssociation>): Promise<PersonPhoneAssociation> {
    return api.post<PersonPhoneAssociation>(`/persons/${personId}/phones`, phone);
  },

  // Update phone number
  async updatePhone(personId: number, phoneId: number, updates: Partial<PersonPhoneAssociation>): Promise<PersonPhoneAssociation> {
    return api.put<PersonPhoneAssociation>(`/persons/${personId}/phones/${phoneId}`, updates);
  },

  // Delete phone number
  async deletePhone(personId: number, phoneId: number): Promise<void> {
    return api.delete(`/persons/${personId}/phones/${phoneId}`);
  },

  // ============ Address Management (SuperAdmin Only) ============
  // Addresses are sensitive PII and only accessible to superAdmin users

  // Get all addresses for a person
  async getAddresses(personId: number): Promise<PersonAddressResponse[]> {
    return api.get<PersonAddressResponse[]>(`/persons/${personId}/addresses`);
  },

  // Add address
  async addAddress(personId: number, address: PersonAddressCreate): Promise<PersonAddressResponse> {
    return api.post<PersonAddressResponse>(`/persons/${personId}/addresses`, address);
  },

  // Update address
  async updateAddress(personId: number, addressId: number, updates: PersonAddressUpdate): Promise<PersonAddressResponse> {
    return api.put<PersonAddressResponse>(`/persons/${personId}/addresses/${addressId}`, updates);
  },

  // Delete address
  async deleteAddress(personId: number, addressId: number): Promise<void> {
    return api.delete(`/persons/${personId}/addresses/${addressId}`);
  },

  // ============ Role Tag Management ============

  // Add role tag (SEOAA record)
  async addRoleTag(personId: number, organizationId: number, roleData: any): Promise<PersonOrganizationRole> {
    return api.post<PersonOrganizationRole>(
      `/persons/${personId}/organizations/${organizationId}/roles`,
      roleData
    );
  },

  // Update role tag
  async updateRoleTag(
    personId: number,
    organizationId: number,
    assignmentId: number,
    updates: any
  ): Promise<PersonOrganizationRole> {
    return api.put<PersonOrganizationRole>(
      `/persons/${personId}/organizations/${organizationId}/roles/${assignmentId}`,
      updates
    );
  },

  // Delete role tag
  async deleteRoleTag(personId: number, organizationId: number, assignmentId: number): Promise<void> {
    return api.delete(`/persons/${personId}/organizations/${organizationId}/roles/${assignmentId}`);
  },

  // ============ Deletion Management (SuperAdmin Only) ============

  // Get cascade preview for person deletion - shows what records would be deleted
  async getCascadePreview(personId: number): Promise<{
    personId: number;
    personName: string;
    willDelete: {
      emails: number;
      phones: number;
      addresses: number;
      identificationCodes: number;
      staffRecord: boolean;
      staffAssignments: number;
      userAccount: boolean;
    };
  }> {
    return api.get(`/persons/${personId}/cascadePreview`);
  },

  // Permanently delete a person and all associated records (SuperAdmin only)
  async deletePerson(personId: number): Promise<{ success: boolean }> {
    return api.delete(`/persons/${personId}`);
  },
};